import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TradingToolSearchSelectProps } from "@/types/tradingTool";
import { cn } from "@/utils/utils";

export default function TradingToolSearchSelect({
  value,
  options,
  onChange,
  placeholder,
  searchPlaceholder,
  className,
  disabled = false,
  id,
  name,
}: TradingToolSearchSelectProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("Type to search");
  const resolvedSearchPlaceholder = searchPlaceholder ?? t("Type to search");
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus();
    }
  }, [isOpen]);

  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.trim().toLowerCase()),
  );

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((open) => !open)}
        className={cn(
          "support-contact-field flex w-full items-center justify-between rounded-[8px] border border-white/10 bg-[#121212] px-3 py-2 text-left font-neue text-sm transition-colors",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        <span className={selectedOption ? "text-white" : "text-white/40"}>
          {selectedOption?.label ?? resolvedPlaceholder}
        </span>
        <ChevronDown
          className={cn(
            "h-4 w-4 shrink-0 text-white/40 transition-transform",
            isOpen && "rotate-180",
          )}
          aria-hidden
        />
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 overflow-hidden rounded-xl border border-white/15 bg-[#0b0b0b] shadow-xl">
          <div className="border-b border-white/10 p-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35"
                aria-hidden
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder={resolvedSearchPlaceholder}
                className="support-contact-field w-full rounded-[8px] border border-white/10 bg-[#121212] py-2 pl-9 pr-3 font-neue text-sm text-white placeholder:text-white/40 focus:outline-none"
              />
            </div>
          </div>

          <ul className="max-h-52 overflow-y-auto hide-scrollbar py-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li key={option.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(option.value)}
                    className={cn(
                      "w-full px-3 py-2 text-left font-neue text-sm transition-colors hover:bg-white/[0.06]",
                      value === option.value
                        ? "bg-white/[0.08] text-white"
                        : "text-white/80",
                    )}
                  >
                    {option.label}
                  </button>
                </li>
              ))
            ) : (
              <li className="px-3 py-3 text-center font-neue text-sm text-white/40">
                {t("No matches found")}
              </li>
            )}
          </ul>
        </div>
      )}

      {name && <input type="hidden" name={name} value={value} readOnly />}
    </div>
  );
}
